import { useEffect, useRef } from 'react'
import { StyleSheet } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { create } from 'zustand'
import { Gesture, GestureDetector } from 'react-native-gesture-handler'
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated'
import { DS } from '../../../design-system'
import { scheduleOnRN } from 'react-native-worklets'

const TOAST_DURATION = 5000

interface ToastNotificationMessage {
  text: string
  /**
   * 0 = no auto-hide (the consumer must dismiss manually). Anything else
   * (including undefined) uses {@link TOAST_DURATION}.
   */
  duration?: number
}

/**
 * Internal queue entry. We track a monotonic id so React's `useEffect` deps
 * change even when two consecutive messages share the same `text` —
 * without this, a back-to-back retry of "Failed to register" would only
 * play the slide-in animation once.
 */
interface ToastQueueEntry extends ToastNotificationMessage {
  id: number
}

interface ToastNotificationStore {
  /**
   * The message currently being rendered. `null` means the queue is idle and
   * the toast is hidden. Components subscribe to this to drive their
   * animation.
   */
  current: ToastQueueEntry | null
  /**
   * Pending messages waiting their turn. FIFO. Cap is a soft guard against a
   * pathological burst (e.g. an effect firing in a loop) — anything beyond
   * the cap is dropped silently rather than blocking the UI for minutes.
   */
  queue: ToastQueueEntry[]
  /**
   * Visibility flag preserved for backwards compatibility with the previous
   * API surface. Callers used to write `setState({ hidden: true })` to
   * dismiss. The new `dismiss()` API is preferred.
   *
   * @deprecated read `current === null` instead.
   */
  hidden: boolean
  /**
   * @deprecated use `show(text)` / read `current?.text`. Kept so legacy
   * direct-`setState` consumers still render — see the shim below.
   */
  notificationMessage: ToastNotificationMessage
  /**
   * Enqueue a message. If the toast is idle, it becomes the visible slot
   * immediately. Otherwise it queues behind the current message and
   * advances when the current one auto-hides / is dismissed. The same text
   * twice in a row enqueues twice — the UI re-animates so the user knows
   * the second instance fired.
   */
  show: (message: ToastNotificationMessage | string) => void
  /**
   * Drop the current message and advance to the next queued entry. Called
   * from the auto-hide timer, from the swipe-to-dismiss gesture, or
   * directly by consumers that need to clear the toast (e.g. the in-game
   * game-toast clearing path).
   */
  dismiss: () => void
  /**
   * @deprecated use `dismiss()`. Routes through `dismiss()` for back-compat.
   */
  hide: () => void
}

/**
 * Bound on the in-flight queue. Generous — a real burst at this depth
 * already indicates a bug somewhere; capping prevents the queue from
 * growing without bound and starving newer messages.
 */
const MAX_QUEUE_LENGTH = 8

let __toastIdSeq = 0
const nextToastId = (): number => ++__toastIdSeq

const normalizeMessage = (message: ToastNotificationMessage | string): ToastQueueEntry => ({
  id: nextToastId(),
  ...(typeof message === 'string' ? { text: message } : message)
})

export const toastNotificationStore = create<ToastNotificationStore>((set, get) => ({
  current: null,
  queue: [],
  hidden: true,
  notificationMessage: { text: '' },

  show: (message) => {
    const entry = normalizeMessage(message)
    if (!entry.text) return
    set((state) => {
      if (state.current === null) {
        // Idle → render immediately.
        return {
          current: entry,
          hidden: false,
          notificationMessage: { text: entry.text, duration: entry.duration }
        }
      }
      // Busy → enqueue behind the current message. Drop if the queue is at
      // cap so we don't pin the UI showing minutes-old messages.
      if (state.queue.length >= MAX_QUEUE_LENGTH) {
        return state
      }
      return { queue: [...state.queue, entry] }
    })
  },

  dismiss: () => {
    set((state) => {
      const [next, ...rest] = state.queue
      if (!next) {
        return {
          current: null,
          queue: [],
          hidden: true,
          notificationMessage: { text: '' }
        }
      }
      return {
        current: next,
        queue: rest,
        hidden: false,
        notificationMessage: { text: next.text, duration: next.duration }
      }
    })
  },

  hide: () => get().dismiss()
}))

/**
 * Subscribe to legacy `setState({ notificationMessage: { text } })` writes
 * and forward them through the queue-aware `show()` path. Without this,
 * direct `setState` writes would clobber the visible message AND lose any
 * queued messages — exactly the regression this redesign is meant to fix.
 *
 * The shim watches for `notificationMessage.text` transitioning to a
 * non-empty value that doesn't match the currently-rendered text. That
 * combination is unambiguously "someone enqueued a new message via the
 * legacy API" — internal state writes from `show`/`dismiss` always set
 * `notificationMessage` *and* `current` in the same patch, so the texts
 * match and we don't re-enqueue.
 */
toastNotificationStore.subscribe((state, prev) => {
  const newText = state.notificationMessage.text
  const currentRendered = state.current?.text ?? ''
  // Same render slot, same legacy field → internal write from show/dismiss; ignore.
  if (newText === currentRendered) return
  // Empty payload (legacy reset to '') → no-op; dismiss() already handled it.
  if (!newText) return
  // Legacy `hidden: true` toggle without changing text → no-op; dismiss() owns this.
  if (state.hidden && newText === prev.notificationMessage.text) return
  // Forward the legacy write through the canonical queue path.
  state.show({ text: newText, duration: state.notificationMessage.duration })
})

export const ToastNotification = () => {
  const current = toastNotificationStore((s) => s.current)
  const dismiss = toastNotificationStore((s) => s.dismiss)
  const text = current?.text ?? ''
  const duration = current?.duration

  const insets = useSafeAreaInsets()
  const finalPosition = insets.top + 10
  const hiddenPosition = -150
  const positionY = useSharedValue(hiddenPosition)
  const opacity = useSharedValue(0)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  // `duration === 0` explicitly disables auto-hide (caller wants a sticky
  // toast). Anything else (including undefined) uses TOAST_DURATION.
  const currentDuration = duration === 0 ? 0 : TOAST_DURATION

  useEffect(() => {
    if (!current) {
      // No active toast — make sure the animation reflects the hidden state.
      positionY.value = withTiming(hiddenPosition, { duration: 250 })
      opacity.value = 0
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      return
    }

    positionY.value = hiddenPosition
    opacity.value = 1
    positionY.value = withTiming(finalPosition, { duration: 500 })

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    if (currentDuration) {
      // @ts-expect-error timeout is React Native flavored
      timeoutRef.current = setTimeout(() => {
        positionY.value = withTiming(hiddenPosition, { duration: 250 })
        // Wait for the slide-out animation before advancing the queue —
        // otherwise the next message slides in over the still-visible old
        // text and the transition looks like a glitch.
        setTimeout(dismiss, 300)
      }, currentDuration)
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
    // `current?.id` covers same-text re-fires (the id always changes); using
    // `current` directly would trigger on any store change including queue
    // updates that don't affect what we render.
  }, [current?.id, currentDuration, finalPosition, dismiss, positionY, opacity])

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: positionY.value }],
    opacity: opacity.value
  }))

  const panGesture = Gesture.Pan()
    .onUpdate((event) => {
      if (event.translationY < finalPosition) {
        positionY.value = event.translationY
      }
    })
    .onEnd((event) => {
      if (event.translationY < finalPosition) {
        positionY.value = withTiming(hiddenPosition, { duration: 250 })
        scheduleOnRN(dismiss)
      }
    })

  if (!text) return null

  return (
    <GestureDetector gesture={panGesture}>
      <Animated.View style={[styles.container, animatedStyle]}>
        <DS.Text textAlign="center" variant="Body Small/Medium">
          {text}
        </DS.Text>
      </Animated.View>
    </GestureDetector>
  )
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    alignSelf: 'center',
    top: 0,
    opacity: 0,
    zIndex: 9999,
    borderRadius: 24,
    minWidth: 160,
    gap: DS.spacing[8],
    paddingHorizontal: DS.spacing[16],
    paddingVertical: DS.spacing[8],
    backgroundColor: DS.palette['Global/White']
  }
})
